import { useCallback, useSyncExternalStore } from 'react';

/**
 * The one definition of the design system's `mobile` breakpoint
 * (`patterns/design-system.md`, Layout). At or above it the frame is the Ink
 * sidebar; below it, one Ink band across the top.
 *
 * `patterns/frontend-conventions.md`, 1 asks for one value read by both CSS and
 * script rather than two definitions that can drift. So the value is written
 * here, published to CSS as a custom property, and the layout switch is driven
 * by the `data-frame-layout` attribute this hook decides — CSS carries no
 * second copy of the number in a media query.
 */
export const MOBILE_BREAKPOINT_PX = 760;

/** Below the breakpoint. 0.02px keeps a 760px viewport on the sidebar side. */
export const SMALL_FRAME_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX - 0.02}px)`;

export type FrameLayout = 'sidebar' | 'band';

/** Publishes the values to CSS, for anything that needs the number itself. */
export function publishBreakpointToCss(root: HTMLElement): void {
  root.style.setProperty('--breakpoint-mobile', `${MOBILE_BREAKPOINT_PX}px`);
  root.style.setProperty('--breakpoint-list-detail', `${LIST_DETAIL_BREAKPOINT_PX}px`);
}

/**
 * One `matchMedia` query as React state. Every breakpoint hook here is built
 * on it, so each reads its value the same way.
 */
function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/**
 * Whether the frame is in its small-screen placement. It decides which controls
 * exist, not only how they look: an overlay is placed by script, so a control
 * hidden with `display: none` would keep its state and its position.
 */
export function useIsSmallFrame(): boolean {
  return useMediaQuery(SMALL_FRAME_QUERY);
}

/**
 * The design system's list-and-detail breakpoint (`patterns/design-system.md`,
 * Layout; `patterns/ui-patterns.md`, 5). At or above it a list screen shows its
 * detail in a pane beside the list; below it the detail is its own view.
 */
export const LIST_DETAIL_BREAKPOINT_PX = 1180;

/** At or above the breakpoint. */
export const LIST_DETAIL_QUERY = `(min-width: ${LIST_DETAIL_BREAKPOINT_PX}px)`;

/** Whether a list screen shows its detail in a pane beside the list. */
export function useIsListDetailPane(): boolean {
  return useMediaQuery(LIST_DETAIL_QUERY);
}

export function frameLayout(isSmallFrame: boolean): FrameLayout {
  return isSmallFrame ? 'band' : 'sidebar';
}
